import {
  KoobButton,
  KoobInputLabel,
  KoobInputNumber,
  KoobInputSelect,
  KoobModal,
  KoobPlaceholder,
  KoobTooltip,
} from '@koob/ui';
import KoobParagraph from 'components/Koob/KoobParagraph';
import KoobTitle from 'components/Koob/KoobTitle';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useAsync } from 'react-async';
import { getHotelsContracts } from '../../../../api/node/contracts';
import { ToggleSection } from '../../../../components';
import { formatDate } from '@koob/dates';
import { parseBoolean } from '../../../../utils';
import { getRoomsDetail } from '../../../../utils/hotelDistribution';
import {
  alignModelRoomsToCurrentPeriods,
  MARGIN_RESET,
  parseMarginValue,
  toBooleanSelectValue,
} from '../../utils';
import {
  hasSeveralContracts,
  SeasonHeader,
} from '../../Edit/Partials/SeasonDistribution';
import { CLOSED } from '../../../Hotels/Hotel/Contracts/EditContract/seasons/seasonModel';

const marginTypeValue = (value) =>
  value === MARGIN_RESET ? MARGIN_RESET : toBooleanSelectValue(value);

// The margin command a level holds, null on "No change".
const levelMargin = (level) => {
  const type = marginTypeValue(level?.isMarginPercent);
  return type === '' ? null : { type, margin: level.margin };
};

const NO_CHANGE = { isMarginPercent: '', margin: '' };

// A level's margin, as in the rooms modal: an empty value means "No change"
// and shows, greyed and dashed, what it inherits from an applied level above;
// a typed value takes the inherited type, or percent, and only then can its
// type be chosen. "Reset (inherit)" is an action of its own, which clears the
// level's margin on every selected connection request. A reset level shows
// the margin it resolves to, once known; typing a margin replaces the reset.
function MarginInputs({
  level,
  inherited,
  resetInherited,
  onChange,
  disabled,
}) {
  const { t } = useTranslation('toConnectionRequest');
  const type = marginTypeValue(level?.isMarginPercent);
  const isReset = type === MARGIN_RESET;
  const ownMargin = !isReset && parseMarginValue(level?.margin) !== null;
  const shown = isReset ? resetInherited : inherited;
  const inheritedType =
    typeof shown?.type === 'boolean' ? shown.type : undefined;
  const typeLabel = (isPercent) =>
    isPercent === false
      ? t('bulkEdit.editor.marginFixed')
      : t('bulkEdit.editor.marginPercent');
  const isTypeInheriting = !ownMargin && inheritedType !== undefined;
  const isMarginInheriting =
    !ownMargin && parseMarginValue(shown?.margin) !== null;

  const setMargin = (margin) =>
    onChange(
      margin === null
        ? NO_CHANGE
        : {
            margin,
            isMarginPercent:
              typeof type === 'boolean' ? type : (inheritedType ?? true),
          },
    );

  return (
    <>
      <KoobInputLabel label={t('bulkEdit.editor.marginValue')}>
        <KoobInputNumber
          min={0}
          value={isReset ? null : (level?.margin ?? null)}
          placeholder={isMarginInheriting ? String(shown.margin) : undefined}
          inherited={isMarginInheriting}
          onValueChange={setMargin}
          disabled={disabled}
        />
      </KoobInputLabel>

      <KoobInputSelect
        label={t('bulkEdit.editor.marginType')}
        placeholder={
          isTypeInheriting
            ? typeLabel(inheritedType)
            : isReset
              ? t('bulkEdit.editor.marginReset')
              : t('bulkEdit.editor.noChange')
        }
        inherited={isTypeInheriting}
        value={ownMargin ? type : ''}
        onValueChange={(isMarginPercent) => onChange({ isMarginPercent })}
        options={[
          { label: typeLabel(true), value: true },
          { label: typeLabel(false), value: false },
        ]}
        disabled={disabled || !ownMargin}
      />

      <div className="pb-1">
        <KoobButton
          variant="ghost"
          size="sm"
          type="button"
          onClick={() =>
            onChange({ isMarginPercent: MARGIN_RESET, margin: '' })
          }
          disabled={disabled || isReset}
        >
          {t('bulkEdit.editor.marginReset')}
        </KoobButton>
      </div>
    </>
  );
}

function MarginFields({ name, inherited, resetInherited }) {
  const [{ value }, , { setValue }] = useField(name);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <MarginInputs
        level={value}
        inherited={inherited}
        resetInherited={resetInherited}
        onChange={(patch) => setValue({ ...value, ...patch })}
      />
    </div>
  );
}

// A room or room × season margin command (KD-3896). "Apply" also makes the
// levels below inherit it; a margin needs its value, or a reset, to be applied.
// Correcting an applied level keeps the apply, clearing it drops the apply.
function LevelMarginFields({
  value,
  inherited,
  resetInherited,
  onChange,
  applyLabel,
  appliedLabel,
  onApply,
  disabled,
}) {
  const level = value ?? {};
  const type = marginTypeValue(level.isMarginPercent);
  const canApply =
    type === MARGIN_RESET ||
    (type !== '' && parseMarginValue(level.margin) !== null);
  const set = (patch) => {
    const next = { ...level, ...patch };
    onChange({
      ...next,
      overrideChildren:
        Boolean(level.overrideChildren) &&
        marginTypeValue(next.isMarginPercent) !== '',
    });
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <MarginInputs
        level={level}
        inherited={inherited}
        resetInherited={resetInherited}
        onChange={set}
        disabled={disabled}
      />

      <div className="pb-1">
        <KoobButton
          variant="primary"
          size="sm"
          type="button"
          onClick={onApply}
          disabled={disabled || !canApply}
        >
          {applyLabel}
        </KoobButton>
      </div>

      {level.overrideChildren && (
        <span className="pb-2 text-xs text-orange-600">{appliedLabel}</span>
      )}
    </div>
  );
}

const indexById = (items, id) => {
  const index = items?.findIndex((item) => String(item?.id) === String(id));
  return index >= 0 ? index : (items?.length ?? 0);
};

function EyeButton({ room, roomsFieldName, roomFieldName, disabled }) {
  const { t } = useTranslation('toConnectionRequest');
  const [{ value: rooms }, , { setValue: setRooms }] = useField(roomsFieldName);
  const [{ value: roomField }, , { setValue: setRoomField }] =
    useField(roomFieldName);
  const roomValue = rooms?.find((r) => String(r?.id) === String(room?.id));

  useEffect(() => {
    if (disabled && roomField && parseBoolean(roomField.enabled) === true) {
      setRoomField({ ...roomField, enabled: 'false' });
    }
  }, [disabled, roomField]);

  const addItem = () => {
    if (roomValue !== undefined) {
      return;
    }

    setRooms([
      ...(rooms ?? []),
      {
        id: room.id,
        enabled: null,
        periods: room.periods,
      },
    ]);
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {roomValue?.enabled === undefined ? (
        <KoobTooltip
          disabled={!disabled}
          tip={t('bulkEdit.editor.periods.periodRequiredForVisibility')}
        >
          <KoobButton
            variant="primary"
            onClick={() => addItem()}
            disabled={disabled}
          >
            <i className="far fa-eye mr-1" />
            {t('bulkEdit.editor.contracts.editButton')}
          </KoobButton>
        </KoobTooltip>
      ) : (
        <KoobInputSelect
          label={t('bulkEdit.editor.visibility')}
          placeholder={t('bulkEdit.editor.noChange')}
          value={toBooleanSelectValue(roomField?.enabled)}
          onValueChange={(value) =>
            setRoomField({ ...roomField, enabled: value })
          }
          options={[
            { label: t('bulkEdit.editor.noChange'), value: '' },
            { label: t('bulkEdit.editor.visible'), value: true },
            { label: t('bulkEdit.editor.invisible'), value: false },
          ]}
          disabled={disabled}
        />
      )}
    </div>
  );
}

function PeriodRow({ period, name, inherited, resetInherited }) {
  const theme = useTheme();
  const { t } = useTranslation('toConnectionRequest');

  return (
    <div className="bg-gray-100 py-4 px-5 rounded">
      <div className="flex justify-between items-center space-x-5">
        <h3
          className={`text-base font-bold text-k-h`}
          style={{ color: theme.gray }}
        >
          {t('periodDates', {
            start: formatDate(period.startAt, 'DD-MM-YYYY'),
            end: formatDate(period.endAt, 'DD-MM-YYYY'),
          })}
        </h3>

        <div>
          <MarginFields
            name={name}
            inherited={inherited}
            resetInherited={resetInherited}
          />
        </div>
      </div>
    </div>
  );
}

function SeasonItem({
  season,
  seasonLevel,
  inherited,
  resetInherited,
  periodsInherited,
  periodsResetInherited,
  onChange,
  periods,
  onApply,
  withDates,
  disabled,
}) {
  const { t } = useTranslation('toConnectionRequest');
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-md border border-gray-200 px-4 py-3 ${
        season.offered ? '' : 'bg-gray-50 opacity-60'
      }`}
    >
      <SeasonHeader
        season={season}
        periods={periods.map(({ period }) => period)}
        withDates={withDates}
      />

      {season.offered && (
        <div className="mt-3 flex flex-col space-y-3">
          <LevelMarginFields
            value={seasonLevel}
            inherited={inherited}
            resetInherited={resetInherited}
            onChange={onChange}
            applyLabel={t('bulkEdit.editor.applyToAllPeriodsOfSeason')}
            appliedLabel={t('bulkEdit.editor.appliedToPeriods')}
            onApply={onApply}
            disabled={disabled}
          />

          {periods.length > 0 && (
            <div>
              <KoobButton
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => setExpanded((current) => !current)}
              >
                <i
                  className={`fa-solid ${
                    expanded ? 'fa-chevron-up' : 'fa-chevron-down'
                  }`}
                />
                {t('distribution.periods', { count: periods.length })}
              </KoobButton>

              {expanded && (
                <div className="mt-2 flex flex-col space-y-3">
                  {periods.map(({ period, name }) => (
                    <PeriodRow
                      key={period.id}
                      period={period}
                      name={name}
                      inherited={periodsInherited}
                      resetInherited={periodsResetInherited}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RoomListItem({ itemField, room }) {
  const { t } = useTranslation('toConnectionRequest');
  const { setFieldValue } = useFormikContext();

  const [{ value: rooms }] = useField(`${itemField}.rooms`);

  const rIndex =
    rooms?.findIndex((r) => String(r?.id) === String(room?.id)) ?? 0;
  const roomIndex = rIndex >= 0 ? rIndex : (rooms?.length ?? 0);

  const roomsFieldName = `${itemField}.rooms`;
  const roomFieldName = `${roomsFieldName}[${roomIndex}]`;

  const [{ value: enabled }] = useField(`${roomFieldName}.enabled`);
  const [{ value: roomValue }] = useField(roomFieldName);
  const [{ value: contracts }] = useField(`${itemField}.contracts`);

  // KD-3896: the room and room × season margin commands.
  const [{ value: levels }] = useField(`${itemField}.distributionRooms`);
  const levelIndex = indexById(levels, room.id);
  const levelName = `${itemField}.distributionRooms[${levelIndex}]`;
  const roomLevel = levels?.[levelIndex];
  const seasonLevelOf = (season) =>
    roomLevel?.seasons?.find(({ id }) => String(id) === String(season.id));

  // The room entry always carries its id, even when only a season is set:
  // the levels are looked up by id.
  const setRoomLevel = (next) =>
    setFieldValue(levelName, { ...next, id: room.id });

  const setSeasonLevel = (season, next) => {
    const seasonLevels = roomLevel?.seasons ?? [];
    const seasonIndex = indexById(seasonLevels, season.id);
    const nextSeasons = [...seasonLevels];
    nextSeasons[seasonIndex] = { ...next, id: season.id };
    setRoomLevel({ ...roomLevel, seasons: nextSeasons });
  };

  const canDisplayPeriod = (period) => {
    if (!contracts) {
      return true;
    }
    const contract = contracts?.find((contract) => {
      return (
        new Date(contract.startAt) <= new Date(period.startAt) &&
        new Date(contract.endAt) >= new Date(period.endAt)
      );
    });
    return parseBoolean(contract?.enabled) !== false;
  };

  const visiblePeriods = room.periods
    .map((period, periodIndex) => ({
      period,
      name: `${roomFieldName}.periods.${periodIndex}`,
    }))
    .filter(({ period }) => canDisplayPeriod(period));
  // A season follows its contract, as its periods do.
  const canDisplaySeason = (season) => {
    const contract = contracts?.find(
      ({ id }) => String(id) === String(season.contractId),
    );
    return (
      season.category !== CLOSED && parseBoolean(contract?.enabled) !== false
    );
  };
  const seasons = room.seasons.filter(canDisplaySeason);

  // The margins entered below an applied level would win over it.
  const clearPeriodMargins = (matches) => {
    if (!roomValue?.periods) return;
    setFieldValue(
      `${roomFieldName}.periods`,
      roomValue.periods.map((period) =>
        matches(period)
          ? { ...period, margin: '', isMarginPercent: '' }
          : period,
      ),
    );
  };

  const applyToRoom = () => {
    setRoomLevel({ ...roomLevel, overrideChildren: true, seasons: [] });
    clearPeriodMargins(() => true);
  };

  // What an applied level hands down to the levels below left on "No change":
  // the nearest level holding a margin, once an apply cleared the periods.
  const roomApplied = Boolean(roomLevel?.overrideChildren);
  const roomMargin = roomApplied ? levelMargin(roomLevel) : null;
  const periodsInheritedOf = (seasonLevel) =>
    seasonLevel?.overrideChildren || roomApplied
      ? (levelMargin(seasonLevel) ?? levelMargin(roomLevel))
      : null;

  // What a reset level resolves to: the margin of the level above, when it is
  // known here. A season left on "No change" keeps its own margin, unknown
  // unless the room apply cleared it; a reset room falls back to the hotel.
  const knownMargin = (margin) =>
    typeof margin?.type === 'boolean' ? margin : null;
  const roomResolved = knownMargin(levelMargin(roomLevel));
  const seasonResolvedOf = (seasonLevel) => {
    const margin = levelMargin(seasonLevel);
    if (margin?.type === MARGIN_RESET) return roomResolved;
    return knownMargin(margin) ?? (roomApplied ? roomResolved : null);
  };

  // A model's periods carry no season: read it from the contract periods.
  const seasonRoomIdOf = (period) =>
    room.periods.find(({ id }) => String(id) === String(period.id))
      ?.seasonRoomId;

  const applyToSeason = (season) => {
    setSeasonLevel(season, {
      ...seasonLevelOf(season),
      overrideChildren: true,
    });
    clearPeriodMargins((period) => seasonRoomIdOf(period) === season.id);
  };

  const isHidden = parseBoolean(enabled) === false;

  return (
    <div className="border p-2 rounded-md">
      <ToggleSection
        key={room.id}
        title={room.name}
        variant="full"
        disabled={!parseBoolean(enabled)}
        rightElement={
          <div>
            <EyeButton
              room={room}
              roomsFieldName={roomsFieldName}
              roomFieldName={roomFieldName}
              disabled={room?.periods?.every(
                (period) => !canDisplayPeriod(period),
              )}
            />
          </div>
        }
      >
        <div className="m-5 flex-col space-y-3">
          {visiblePeriods.length > 0 && (
            <div className="rounded-md bg-gray-50 px-4 py-3">
              <div className="mb-2 text-sm font-semibold text-gray-900">
                {seasons.length > 0
                  ? t('distribution.allSeasons')
                  : t('bulkEdit.editor.roomLevel')}
              </div>
              <LevelMarginFields
                value={roomLevel}
                onChange={setRoomLevel}
                applyLabel={
                  seasons.length > 0
                    ? t('bulkEdit.editor.applyToAllSeasonsAndPeriods')
                    : t('bulkEdit.editor.applyToAllPeriods')
                }
                appliedLabel={
                  seasons.length > 0
                    ? t('bulkEdit.editor.appliedBelow')
                    : t('bulkEdit.editor.appliedToPeriods')
                }
                onApply={applyToRoom}
                disabled={isHidden}
              />
            </div>
          )}

          {seasons.map((season) => (
            <SeasonItem
              key={season.id}
              season={season}
              seasonLevel={seasonLevelOf(season)}
              inherited={roomMargin}
              resetInherited={roomResolved}
              periodsInherited={periodsInheritedOf(seasonLevelOf(season))}
              periodsResetInherited={seasonResolvedOf(seasonLevelOf(season))}
              onChange={(next) => setSeasonLevel(season, next)}
              periods={visiblePeriods.filter(
                ({ period }) => period.seasonRoomId === season.id,
              )}
              onApply={() => applyToSeason(season)}
              withDates={hasSeveralContracts(seasons)}
              disabled={isHidden}
            />
          ))}

          {visiblePeriods
            .filter(({ period }) => !period.seasonRoomId)
            .map(({ period, name }) => (
              <PeriodRow
                key={period.id}
                period={period}
                name={name}
                inherited={roomMargin}
                resetInherited={roomResolved}
              />
            ))}
        </div>
      </ToggleSection>
    </div>
  );
}

function RoomList({ hotel, itemField }) {
  const { t } = useTranslation('toConnectionRequest');

  const { data, isLoading } = useAsync({
    promiseFn: getHotelsContracts,
    hotelId: hotel.id,
  });
  const contracts = data?.data;

  const rooms = getRoomsDetail(data, hotel.periods ?? []) ?? [];

  // KD-3622: the margins in the formik rooms come from the model and may be
  // keyed by period ids that were recreated since (contract republished).
  // Realign them onto the current periods so the fields displayed below edit
  // periods that actually exist.
  const [{ value: formikRooms }, , { setValue: setFormikRooms }] = useField(
    `${itemField}.rooms`,
  );
  useEffect(() => {
    if (isLoading || !rooms.length || !formikRooms?.length) {
      return;
    }
    setFormikRooms(alignModelRoomsToCurrentPeriods(formikRooms, rooms));
    // Realign once per contract load: the alignment is idempotent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, contracts]);

  return (
    <>
      {isLoading || contracts?.length > 0 ? (
        <div className="flex-col space-y-5">
          {isLoading && (
            <div className="flex-col space-y-5">
              <KoobPlaceholder className="h-24 w-full rounded-md" />
              <KoobPlaceholder className="h-24 w-full rounded-md" />
              <KoobPlaceholder className="h-24 w-full rounded-md" />
            </div>
          )}

          {!isLoading &&
            rooms?.map((room) => (
              <RoomListItem key={room.id} room={room} itemField={itemField} />
            ))}
        </div>
      ) : (
        <div className="h-60 flex flex-col justify-center items-center">
          <div className="text-center">
            <i className="far fa-file-signature text-4xl text-gray-300" />

            <div className="mt-3 font-medium text-gray-500">
              {t('bulkEdit.editor.contracts.noContracts')}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function BulkEditorItemRooms({ hotel, itemField, disabled }) {
  const { t } = useTranslation('toConnectionRequest');

  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <KoobButton
        variant="primary"
        size="sm"
        disabled={disabled}
        onClick={() => setShowModal(true)}
      >
        <i className="fa-regular fa-bed text-lg" />
      </KoobButton>

      {showModal && (
        <KoobModal
          open={true}
          setOpen={() => setShowModal(false)}
          widthClass="sm:max-w-xl xl:max-w-4xl overflow-visible"
        >
          <KoobTitle>{t('bulkEdit.editor.periods.title')}</KoobTitle>
          <KoobParagraph>{hotel.name}</KoobParagraph>

          <div className="mt-5">
            {showModal && <RoomList hotel={hotel} itemField={itemField} />}
          </div>
        </KoobModal>
      )}
    </>
  );
}
